import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, ChevronDown, Sparkles } from 'lucide-react';
import { COMPANY, DESTINATIONS, HOLIDAY_TYPES, SUB_REGIONS } from '../../mock';
import { Button } from '../ui/button';

const LOGO_URL = '/images/Dashboard/Assets/Images/profile_img/mct p2.png';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoverMenu, setHoverMenu] = useState(null);
  const [mobileSub, setMobileSub] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => { setOpen(false); setMobileSub(null); }, [location.pathname]);

  return (
    <>
      <div className="hidden md:block bg-brand-blue text-white text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-5 text-white/80">
            <span className="flex items-center gap-1.5"><Sparkles size={12} className="text-brand-orange animate-pulse"/> Flat 25% OFF + Free Candlelight Dinner · Limited Shimla 2026 slots</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-1.5 hover:text-brand-orange transition-colors font-medium"><Phone size={13}/>{COMPANY.phone}</a>
            <span className="text-white/40">|</span>
            <span className="font-medium">Himachal Govt. Registered · {COMPANY.yearsOfExperience}+ Yrs</span>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-[68px] md:h-[78px] flex items-center justify-between gap-2 md:gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="Mountains Chain Travel" className="h-7 md:h-11 lg:h-14 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" onMouseLeave={() => setHoverMenu(null)}>
            <NavItem to="/" label="Home" />
            <DropItem label="Destinations" open={hoverMenu === 'dest'} onEnter={() => setHoverMenu('dest')}>
              <MegaDestinations />
            </DropItem>
            <DropItem label="Holidays" open={hoverMenu === 'hol'} onEnter={() => setHoverMenu('hol')}>
              <MegaHolidays />
            </DropItem>
            <NavItem to="/packages" label="Packages" />
            <NavItem to="/blog" label="Blog" />
            <NavItem to="/about" label="About" />
            <NavItem to="/contact" label="Contact" />
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <a href={`tel:${COMPANY.phone}`} className="hidden xl:flex items-center gap-2 px-3 py-2 rounded-lg border border-brand-blue/15 text-brand-blue hover:bg-brand-grey transition-colors text-[13px] font-semibold"><Phone size={15}/>Call</a>
            <Button 
              onClick={() => window.dispatchEvent(new CustomEvent('mct:openEnquiry'))}
              className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold btn-glow rounded-lg px-3.5 sm:px-5 h-9 md:h-10 shadow-lg shadow-brand-orange/20 animate-pulse-subtle text-[12.5px] sm:text-[13.5px]"
            >
              Get Free Quote
            </Button>
            <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d love to plan a trip with you.\n\nPlease share available tour packages and pricing.\n\nThank you!')}`} target="_blank" rel="noreferrer" className="hidden sm:inline-flex">
              <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold rounded-lg px-4 h-10"><MessageCircle size={16} className="mr-1.5"/>WhatsApp</Button>
            </a>
            <button className="lg:hidden p-2 -mr-2 text-brand-blue" onClick={() => setOpen(!open)} aria-label="menu">{open ? <X size={24}/> : <Menu size={24}/>}</button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t bg-white animate-slide-in max-h-[80vh] overflow-y-auto">
            <div className="px-5 py-4 flex flex-col gap-1">
              <MLink to="/">Home</MLink>
              <MSub label="Destinations" active={mobileSub==='d'} onClick={() => setMobileSub(mobileSub==='d'?null:'d')}>
                <div className="px-4 py-1 text-[10.5px] font-bold text-brand-orange uppercase tracking-wider">State Portals</div>
                <Link to="/destinations/himachal-pradesh" className="block py-2 pl-4 text-brand-blue/75 text-[14px]">Himachal Pradesh</Link>
                <Link to="/domestic/jammu-kashmir" className="block py-2 pl-4 text-brand-blue/75 text-[14px]">Jammu & Kashmir</Link>
                <Link to="/domestic/leh-ladakh" className="block py-2 pl-4 text-brand-blue/75 text-[14px]">Ladakh (UT)</Link>
                <Link to="/domestic/uttarakhand" className="block py-2 pl-4 text-brand-blue/75 text-[14px]">Uttarakhand</Link>
                <Link to="/domestic/rajasthan" className="block py-2 pl-4 text-brand-blue/75 text-[14px]">Rajasthan</Link>
                <div className="px-4 py-1 mt-2 text-[10.5px] font-bold text-brand-orange uppercase tracking-wider">Explore by Region</div>
                {SUB_REGIONS.map(r => <Link key={r.slug} to={`/explore/${r.slug}`} className="block py-2 pl-4 text-brand-blue/75 text-[14px]">{r.name} <span className="text-brand-blue/40 text-[11px]">· {r.state}</span></Link>)}
                <Link to="/destinations" className="block py-2 pl-4 text-brand-orange font-semibold text-[14px]">View all →</Link>
              </MSub>
              <MSub label="Holidays" active={mobileSub==='h'} onClick={() => setMobileSub(mobileSub==='h'?null:'h')}>
                {HOLIDAY_TYPES.map(h => <Link key={h.slug} to={h.slug === 'international' ? '/international' : h.slug === 'domestic' ? '/domestic' : h.slug === 'self-drive' ? '/self-drive' : `/holidays/${h.slug}`} className="block py-2 pl-4 text-brand-blue/75 text-[14px]">{h.name}</Link>)}
              </MSub>
              <MLink to="/packages">Packages</MLink>
              <MLink to="/blog">Blog</MLink>
              <MLink to="/about">About</MLink>
              <MLink to="/contact">Contact</MLink>
              <div className="flex gap-2 pt-3 mt-2 border-t">
                <a href={`tel:${COMPANY.phone}`} className="flex-1"><Button variant="outline" className="w-full border-brand-blue/20 text-brand-blue"><Phone size={16} className="mr-1.5"/>Call</Button></a>
                <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d love to plan a trip with you.\n\nPlease share available tour packages.\n\nThank you!')}`} target="_blank" rel="noreferrer" className="flex-1"><Button className="w-full bg-brand-orange text-white"><MessageCircle size={16} className="mr-1.5"/>WhatsApp</Button></a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}

function NavItem({ to, label }) {
  return (
    <NavLink to={to} end={to==='/'} className={({isActive}) => `relative px-3.5 py-2 rounded-lg text-[14px] font-medium transition-colors duration-300 group ${isActive ? 'text-brand-orange' : 'text-brand-blue hover:text-brand-orange'}`}>
      {({isActive}) => (
        <>
          <span className="relative z-10">{label}</span>
          <span className={`absolute left-3.5 right-3.5 -bottom-0.5 h-[2px] bg-brand-orange rounded-full origin-left transition-transform duration-300 ease-out ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} aria-hidden />
        </>
      )}
    </NavLink>
  );
}
function DropItem({ label, open, onEnter, children }) {
  return (
    <div className="relative" onMouseEnter={onEnter}>
      <button className={`relative px-3.5 py-2 rounded-lg text-[14px] font-medium flex items-center gap-1 transition-colors duration-300 group ${open ? 'text-brand-orange' : 'text-brand-blue hover:text-brand-orange'}`}>
        <span className="relative z-10">{label}</span>
        <ChevronDown size={14} className={`transition-transform duration-300 ${open?'rotate-180':''}`}/>
        <span className={`absolute left-3.5 right-7 -bottom-0.5 h-[2px] bg-brand-orange rounded-full origin-left transition-transform duration-300 ease-out ${open ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} aria-hidden />
      </button>
      {open && <div className="absolute top-full left-0 pt-3 z-50"><div className="bg-white rounded-2xl shadow-premium border border-brand-greyDark overflow-hidden animate-fade-up">{children}</div></div>}
    </div>
  );
}
function MLink({ to, children }) { return <NavLink to={to} end={to==='/'} className={({isActive}) => `px-3 py-3 rounded-lg text-[15px] font-medium ${isActive ? 'bg-brand-grey text-brand-orange' : 'text-brand-blue'}`}>{children}</NavLink>; }
function MSub({ label, active, onClick, children }) {
  return (
    <div>
      <button onClick={onClick} className="w-full px-3 py-3 rounded-lg text-[15px] font-medium text-brand-blue flex items-center justify-between">{label}<ChevronDown size={16} className={`transition-transform ${active?'rotate-180':''}`}/></button>
      {active && <div className="pl-2 pb-2 flex flex-col">{children}</div>}
    </div>
  );
}
function MegaDestinations() {
  const portals = [
    { slug: 'himachal-pradesh', name: 'Himachal Pradesh', to: '/destinations/himachal-pradesh' },
    { slug: 'jammu-kashmir', name: 'Jammu & Kashmir', to: '/domestic/jammu-kashmir' },
    { slug: 'leh-ladakh', name: 'Ladakh (UT)', to: '/domestic/leh-ladakh' },
    { slug: 'uttarakhand', name: 'Uttarakhand', to: '/domestic/uttarakhand' },
    { slug: 'rajasthan', name: 'Rajasthan', to: '/domestic/rajasthan' },
  ];
  return (
    <div className="p-5 w-[min(820px,90vw)] grid grid-cols-3 gap-x-6 gap-y-1">
      <div className="col-span-3 flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold tracking-[0.2em] text-brand-orange uppercase">Quick links</div>
        <Link to="/destinations" className="text-[11.5px] font-semibold text-brand-blue/65 hover:text-brand-orange transition-colors">View all →</Link>
      </div>

      <div className="col-span-1">
        <div className="text-[10.5px] font-bold tracking-[0.18em] text-brand-blue/55 uppercase mb-2 px-1">State Portals</div>
        <div className="space-y-0.5">
          {portals.map(p => (
            <Link key={p.slug} to={p.to} className="block px-2.5 py-2 rounded-lg text-[13.5px] font-medium text-brand-blue hover:bg-brand-grey hover:text-brand-orange transition-colors">{p.name}</Link>
          ))}
          <Link to="/international" className="block px-2.5 py-2 rounded-lg text-[13.5px] font-medium text-brand-blue hover:bg-brand-grey hover:text-brand-orange transition-colors">International</Link>
        </div>
      </div>

      <div className="col-span-2">
        <div className="text-[10.5px] font-bold tracking-[0.18em] text-brand-blue/55 uppercase mb-2 px-1">Explore by Region</div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
          {SUB_REGIONS.map(r => (
            <Link key={r.slug} to={`/explore/${r.slug}`} className="group flex items-center gap-3 p-2 rounded-lg hover:bg-brand-grey transition-colors">
              <img src={r.image} alt={r.name} className="w-11 h-11 rounded-lg object-cover shrink-0"/>
              <div className="min-w-0">
                <div className="font-poppins font-semibold text-brand-blue text-[13.5px] leading-tight group-hover:text-brand-orange transition-colors">{r.name}</div>
                <div className="text-[10.5px] text-brand-blue/55">{r.state}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
function MegaHolidays() {
  return (
    <div className="p-5 w-[min(520px,90vw)] grid grid-cols-2 gap-1">
      <div className="col-span-2 text-[11px] font-bold tracking-[0.2em] text-brand-orange uppercase mb-2">Holiday by Theme</div>
      {HOLIDAY_TYPES.map(h => (
        <Link key={h.slug} to={h.slug === 'international' ? '/international' : h.slug === 'domestic' ? '/domestic' : h.slug === 'self-drive' ? '/self-drive' : `/holidays/${h.slug}`} className="flex items-center gap-2.5 p-2.5 rounded-lg hover:bg-brand-grey transition-colors">
          <span className="text-xl" aria-hidden>{h.emoji}</span>
          <div>
            <div className="font-semibold text-brand-blue text-[13.5px]">{h.name}</div>
            <div className="text-[11px] text-brand-blue/55">{h.tagline}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}
